/* eslint-disable react-refresh/only-export-components */
import { useMemo } from 'react';

import { createStateAggregatorContext, useCreateStateAggregator } from '#utils/state-aggregator-context.js';

import type { HilPackage, HilPackageFamily } from '@cadstrom/backend/shared';
import type { Templating } from '@cadstrom/cadstrom-shared';
import type { DatasheetContentLabelIds, Utils } from '@cadstrom/sigma-engine-shared';
import type { ReactNode } from 'react';
import type { ZodArray, ZodObject, ZodType } from 'zod';
import type { MessageHistory } from './ai-workbench-utils.js';

export interface IAiExtractionRendererProps<T extends object | Array<object>> {
	data: T;
	updateData: (data: T) => void;
}

export type IAiExtractionRenderer<T extends object | Array<object>> = React.ElementType<IAiExtractionRendererProps<T>>;

/** The current, operator-readable target of a directive row. */
export interface DirectiveTarget {
	sectionSlug: string;
	text: string;
}

export interface _IAiExtractionContextValue<
	T extends object | Array<object> = object,
	E extends object | Array<object> = T,
> {
	configId: string;
	configName: string;
	extractionSchema?: E extends Array<infer U> ?
		U extends object ?
			ZodArray<ZodObject> & ZodType<U[]>
		:	never
	:	ZodObject & ZodType<E>;
	postProcessedSchema?: ZodObject & ZodType<T>;
	postProcessExtractedData?: (data: E) => T | Promise<T>;
	systemPrompt?: string;
	defaultPrompt: string;
	defaultModel?: string;
	defaultTemperature?: number;
	defaultPageLabels?: DatasheetContentLabelIds[];
	renderContents?: IAiExtractionRenderer<T>;
	appendContents?: (data: T) => void | Promise<void>;
	replaceContents?: (data: T) => void | Promise<void>;
	replaceHint?: ReactNode;
	/**
	 * When set, the apply buttons render disabled with this message as their
	 * tooltip. Set it when applying cannot succeed in the current state, so the prerequisite
	 * is visible before clicking rather than an error after.
	 */
	applyDisabledReason?: string;
	/**
	 * Transforms raw directive-table row objects into the processed `T` type before the
	 * `replaceContents` or `appendContents` callback is called by the directive apply bar.
	 *
	 * Mirrors `postProcessExtractedData` but for the directive row format, which uses
	 * signal names instead of signal IDs. Each extraction context that also receives directive
	 * table data should implement this to resolve names → IDs and reshape to `T`.
	 */
	processDirectiveData?: (data: object) => object | Promise<object>;
	/**
	 * Explains a partial directive-table apply. `undefined` keeps the shared signal-resolution
	 * warning, `null` means this context reports rejected rows itself, and a node replaces the
	 * shared warning with context-specific guidance.
	 */
	partialDirectiveApplyMessage?: ReactNode | null;
	/**
	 * Looks up the current, operator-readable target of a directive row by its internal id.
	 * Return `undefined` when that target is no longer present in the active package.
	 */
	getDirectiveTarget?: (id: string) => DirectiveTarget | undefined;
	contextTemplates?: IAiExtractionTemplate[];
	/** Whether to use the extraction schema by default (vs. freeform text schema) */
	useExtractionSchemaDefault?: boolean;
	/**
	 * If true, wrap the schema with page citations (relevantPageNumbers field).
	 * Use this for contexts that need to reference specific pages in datasheets.
	 * @default false
	 */
	includeRelevantPageNumbers?: boolean;
	/** Datasheets for contexts that don't have packages (like PDF viewer) */
	datasheets?: Array<{ name: string; fileId: string; url: string }>;
	/** Callback to set the location in a pdf viewer */
	jumpToPage?: ({ page, offset, key }: { page: number; offset?: number; key: number }) => void;
	/** Callback executed for each message in the AI workbench */
	messageHistoryCallback?: (messageHistory: MessageHistory) => void;
	/** Tools to make available to the AI workbench */
	tools?: string[];
}

// This type conditional type is trick to make the postProcessExtractedData field required if T !== E
export type IAiExtractionContextValue<T extends object | Array<object> = object, E extends object | Array<object> = T> =
	E extends T ? _IAiExtractionContextValue<T, E>
	:	Required<Pick<_IAiExtractionContextValue<T, E>, 'postProcessExtractedData'>> &
			Omit<_IAiExtractionContextValue<T, E>, 'postProcessExtractedData'>;

export interface IAiExtractionTemplate extends Templating.Substitution {
	/**
	 * A description of the template. This is used to help the user understand what the template is for.
	 */
	description: string;

	/**
	 * If set, this value is displayed in the UI instead of the raw value.
	 */
	displayValue?: Utils.ThunkValue<string>;
}

interface IAiExtractionContextRoot {
	value: IAiExtractionContextValue;
	pkgs?: HilPackage[];
	pkgFamily?: HilPackageFamily;
	templates: IAiExtractionTemplate[];
}

export const {
	context: AiExtractionContext,
	AddStateValue: AddAiExtractionValue,
	useStateAggregatorAllValues: useAiExtractionContextAllValues,
	useStateAggregatorLastValue: useAiExtractionContextLastValue,
	useStateAggregatorProxy: useAiExtractionContextProxy,
} = createStateAggregatorContext<IAiExtractionContextRoot>();

export function SetAiExtractionContext<
	T extends object | Array<object> = object,
	E extends object | Array<object> = T,
>(props: { value: IAiExtractionContextValue<T, E> }) {
	return AddAiExtractionValue({ okey: 'value', value: props.value as IAiExtractionContextValue });
}

export function AddAiExtractionContextPkgs({ value }: { value: HilPackage[] }) {
	return <AddAiExtractionValue okey='pkgs' value={value} />;
}
export function AddAiExtractionContextPkgFamily({ value }: { value: HilPackageFamily }) {
	return <AddAiExtractionValue okey='pkgFamily' value={value} />;
}
export function AddAiExtractionContextTemplates({ value }: { value: IAiExtractionTemplate[] }) {
	return <AddAiExtractionValue okey='templates' value={value} />;
}
export const useAiExtractionContextTemplates: () => IAiExtractionTemplate[] = () =>
	useAiExtractionContextAllValues('templates').flat();
export const useAiExtractionContextPkgs = () => useAiExtractionContextLastValue('pkgs');
export const useAiExtractionContextPkgFamily = () => useAiExtractionContextLastValue('pkgFamily');

/**
 * Hook to generate storage key prefix from AI extraction context
 */
export const useAiExtractionContextStorageKey = (configName?: string) => {
	const pkgs = useAiExtractionContextPkgs();
	const pkgFamily = useAiExtractionContextPkgFamily();
	const contextValue = useAiExtractionContextLastValue('value');

	return useMemo(() => {
		const name = configName ?? contextValue?.configName ?? 'AiChat';
		const id = pkgs?.length === 1 || !pkgFamily ? pkgs?.[0]?.id : pkgFamily.id;
		return `AiExtractionWorkbench-${id}-${name}`;
	}, [pkgs, pkgFamily, contextValue, configName]);
};

export const useAiExtractionContext = () => useCreateStateAggregator<IAiExtractionContextRoot>();
